import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";

import PublicLayout from "./components/PublicLayout";
import AppLayout from "./components/AppLayout";
import Home from "./pages/Home";
import Programs from "./pages/Programs";
import Videos from "./pages/Videos";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Assessment from "./pages/Assessment";
import Booking from "./pages/Booking";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Workouts from "./pages/Workouts";
import WorkoutDetail from "./pages/WorkoutDetail";
import Nutrition from "./pages/Nutrition";
import Progress from "./pages/Progress";
import Challenges from "./pages/Challenges";
import Coach from "./pages/Coach";
import Community from "./pages/Community";
import Calendar from "./pages/Calendar";
import Admin from "./pages/Admin";
import ProgramDetail from "./pages/ProgramDetail";
import WorkoutSession from "./pages/WorkoutSession";
import { PaymentSuccess, PaymentFailed, PaymentPending } from "./pages/PaymentResult";
import MyPrograms from "./pages/MyPrograms";
import ProgramPlayer from "./pages/ProgramPlayer";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-center" theme={theme} richColors closeButton />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/videos" element={<Videos />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<BlogDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/assessment" element={<Assessment />} />
          <Route path="/booking" element={<Booking />} />
          <Route path="/programs/:slug" element={<ProgramDetail />} />
        </Route>

        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/failed" element={<PaymentFailed />} />
        <Route path="/payment/pending" element={<PaymentPending />} />
        <Route path="/app/programs/:eid/day/:day/workout" element={<WorkoutSession />} />
        <Route path="/workout-preview/:wid" element={<WorkoutSession />} />

        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        <Route path="/app" element={<AppLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="workouts" element={<Workouts />} />
          <Route path="workouts/:id" element={<WorkoutDetail />} />
          <Route path="nutrition" element={<Nutrition />} />
          <Route path="progress" element={<Progress />} />
          <Route path="challenges" element={<Challenges />} />
          <Route path="programs" element={<MyPrograms />} />
          <Route path="programs/:eid" element={<ProgramPlayer />} />
          <Route path="community" element={<Community />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="coach" element={<Coach />} />
          <Route path="booking" element={<Booking />} />
        </Route>

        <Route path="/admin" element={<Admin />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <AppToaster />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}
